import { useCallback } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';

/**
 * 智能返回 Hook
 *
 * 當瀏覽器有歷史記錄時，返回上一頁
 * 當瀏覽器沒有歷史記錄時（如直接訪問深層URL），導航到URL的上一層
 *
 * @returns {Function} 返回函數
 *
 * @example
 * const goBack = useSmartBack();
 * <Button onClick={goBack}>返回</Button>
 */
export default function useSmartBack(): () => void {
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = useCallback((): void => {
    // 檢查是否有歷史記錄可以回退
    // window.history.length > 1 表示有歷史記錄（至少有當前頁 + 上一頁）
    if (window.history.state && window.history.state.idx > 0) {
      // 有歷史記錄，正常返回
      navigate(-1);
    } else {
      // 沒有歷史記錄，導航到URL的上一層
      const pathSegments = location.pathname.split('/').filter(Boolean);

      if (pathSegments.length > 0) {
        // 移除最後一個段落
        pathSegments.pop();

        // 構建新的路徑
        const newPath = pathSegments.length > 0
          ? `/${pathSegments.join('/')}`
          : '/';

        navigate(newPath);
      } else {
        // 如果已經在根路徑，導航到首頁
        navigate('/');
      }
    }
  }, [navigate, location]);

  return goBack;
}
